import {calculateErrorMessage} from 'common/helpers'
import {Paperclip, Send} from 'lucide-react'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'

import {useVoiceDictation} from '../hooks/useVoiceDictation'
import NativeVoiceListeningIndicator from '../voice/NativeVoiceListeningIndicator'
import VoiceMicButton from '../voice/VoiceMicButton'

interface ChatInputProps {
  onSend: (text: string) => void
  theme: 'light' | 'dark'
  onFocus?: () => void
  disabled?: boolean
  value?: string
  onChangeValue?: (v: string) => void
  externalInputRef?: React.RefObject<HTMLInputElement> | React.MutableRefObject<HTMLInputElement>
  inputType?: 'text' | 'phone'
  phoneOptions?: any[]
  showAttachment?: boolean
  maxLength?: number
  /** False while the chat panel is hidden. The widget stays mounted when closed, so without this
   *  the microphone would keep listening — and stay "listening" on reopen. */
  isActive?: boolean
}

/** Appends dictated speech to whatever the user had already typed, instead of replacing it. */
function composeSpokenText(baseText: string, spokenText: string): string {
  const base = baseText.trim()
  const spoken = spokenText.trim()
  if (!base) return spoken
  if (!spoken) return base
  return `${base} ${spoken}`
}

const DEFAULT_MAX_LENGTH = 2000

/** How long a dictation error (e.g. microphone denied) stays visible under the input. */
const VOICE_ERROR_VISIBLE_MS = 6000

const LOW_CONFIDENCE_BORDER = '#f1bc00'

const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  theme,
  onFocus,
  disabled = false,
  value,
  onChangeValue,
  externalInputRef,
  inputType = 'text',
  phoneOptions,
  showAttachment = true,
  maxLength = DEFAULT_MAX_LENGTH,
  isActive = true,
}) => {
  const intl = useIntl()

  const [internalValue, setInternalValue] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [selectedDialCode, setSelectedDialCode] = useState('')
  const [showLimitError, setShowLimitError] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const inputElementRef:
    | React.RefObject<HTMLInputElement>
    | React.MutableRefObject<HTMLInputElement> =
    (externalInputRef as
      | React.RefObject<HTMLInputElement>
      | React.MutableRefObject<HTMLInputElement>) || inputRef

  const [sendHover, setSendHover] = useState(false)

  // Set when the last dictation came back with low confidence; cleared as soon as the user edits or
  // sends, since either means they have read it.
  const [checkTranscript, setCheckTranscript] = useState(false)

  const isPhone = inputType === 'phone'
  const isControlled = typeof value !== 'undefined' && typeof onChangeValue === 'function'
  const displayedValue = isControlled ? value! : internalValue

  // What the input held when dictation started. Speech is appended to it, so a second utterance
  // (the recogniser restarts after every pause) extends the message instead of wiping it.
  const baseValueRef = useRef('')

  const setValue = useCallback(
    (next: string) => {
      if (isControlled) {
        onChangeValue?.(next)
      } else {
        setInternalValue(next)
      }
    },
    [isControlled, onChangeValue]
  )

  // Read through a ref so the speech callbacks always see the current limit.
  const maxLengthRef = useRef(maxLength)
  maxLengthRef.current = maxLength
  const clampToMaxLength = useCallback((text: string) => text.slice(0, maxLengthRef.current), [])

  const {
    status: dictationStatus,
    isRecording,
    isTranscribing,
    isSupported: isVoiceSupported,
    errorKey: voiceErrorKey,
    start: startRecording,
    stop: stopRecording,
    cancel: cancelRecording,
  } = useVoiceDictation({
    // Dictation fills the input instead of sending: the user reads the transcription back and
    // presses Send themselves, so a misheard word never lands in the conversation unchecked.
    onTranscribed: (text, meta) => {
      const composed = composeSpokenText(baseValueRef.current, text)
      baseValueRef.current = ''
      if (!composed) return

      // The SERVER decides what "low" means and what an unscored transcript
      // counts as; the composer only renders the verdict. Two copies of that
      // threshold is one too many, and this one could not see which engine
      // answered.
      setCheckTranscript(meta?.lowConfidence === true)
      setValue(clampToMaxLength(composed))
      setShowLimitError(composed.length > maxLengthRef.current)
      // The stop button is swapped for the send button in the same commit — focus after paint.
      requestAnimationFrame(() => inputElementRef.current?.focus())
    },
  })

  // Recording and transcribing are both "busy" for the composer, but only recording holds the mic.
  const isDictating = isRecording || isTranscribing

  const handleVoiceToggle = () => {
    if (isRecording) {
      void stopRecording()
    } else if (!isTranscribing) {
      baseValueRef.current = displayedValue
      void startRecording()
    }
  }

  const isDictatingRef = useRef(isDictating)
  isDictatingRef.current = isDictating

  // The chat panel only hides on close — release the microphone, drop the recording, and put back
  // whatever the user had typed before they started dictating.
  useEffect(() => {
    if (isActive || !isDictatingRef.current) return
    const base = baseValueRef.current
    baseValueRef.current = ''
    cancelRecording()
    setValue(base)
  }, [cancelRecording, isActive, setValue])

  // Stays available once there is text, so dictation can be appended to what is already in the
  // field instead of being a one-shot that only works on an empty input.
  const showMicButton = isVoiceSupported && !isPhone && !isDictating
  // Send sits next to the mic once there is something to send, and stands in for the mic entirely
  // when recording is unavailable.
  const showSendButton = !isDictating && (!showMicButton || displayedValue.trim().length > 0)

  // Dictation failures are shown right under the composer, next to the button that was
  // pressed. They used to be dispatched as a Redux bot message, which only the generic
  // ChatbotPage renders — in the assistant widget a denied microphone therefore failed
  // silently, and the mic looked like a mute toggle that did nothing.
  const [voiceErrorText, setVoiceErrorText] = useState<string | null>(null)

  useEffect(() => {
    if (!voiceErrorKey) {
      setVoiceErrorText(null)
      return
    }
    setVoiceErrorText(intl.formatMessage({id: voiceErrorKey}))
    const timer = window.setTimeout(() => setVoiceErrorText(null), VOICE_ERROR_VISIBLE_MS)
    return () => window.clearTimeout(timer)
  }, [voiceErrorKey, intl])

  const normalizedPhoneOptions = React.useMemo(() => {
    const raw =
      Array.isArray(phoneOptions) && phoneOptions.length > 0
        ? phoneOptions
        : ['+92', '+1', '+44', '+91']
    const codes = raw
      .map((opt: any) => {
        if (typeof opt === 'string') return opt
        return opt?.code || opt?.dialCode || opt?.value || opt?.label || opt?.id
      })
      .filter(Boolean)
    return codes.length > 0 ? codes : ['+92']
  }, [phoneOptions])

  useEffect(() => {
    const defaultCode =
      normalizedPhoneOptions.find((code) => code === '+92') || normalizedPhoneOptions[0] || ''
    setSelectedDialCode(defaultCode)
  }, [normalizedPhoneOptions])

  useEffect(() => {
    setShowLimitError(false)
    if (isPhone) {
      setInternalValue('')
      if (isControlled) onChangeValue?.('')
      setPhoneNumber('')
    } else {
      setPhoneNumber('')
    }
  }, [inputType, isControlled, onChangeValue, isPhone])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isPhone) {
      const trimmedNumber = phoneNumber.trim()
      if (disabled || !trimmedNumber || showLimitError) return
      const dial = (selectedDialCode || '').trim()
      const payload = `${dial} ${trimmedNumber}`.trim()
      onSend(payload)
      if (isControlled) onChangeValue?.('')
      setPhoneNumber('')
      setShowLimitError(false)
      return
    }

    if (disabled || !displayedValue.trim() || showLimitError) return

    setCheckTranscript(false)
    onSend(displayedValue.trim())
    if (isControlled) {
      onChangeValue?.('')
    } else {
      setInternalValue('')
    }
    setShowLimitError(false)
    inputElementRef.current?.focus()
  }

  const handleChange = (val: string) => {
    setCheckTranscript(false)
    if (val.length > maxLength) {
      val = val.slice(0, maxLength)
      setShowLimitError(true)
    } else {
      setShowLimitError(false)
    }
    if (isControlled) {
      onChangeValue?.(val)
    } else {
      setInternalValue(val)
    }
  }

  const handlePhoneChange = (val: string) => {
    if (val.length > maxLength) {
      val = val.slice(0, maxLength)
      setShowLimitError(true)
    } else {
      setShowLimitError(false)
    }
    setPhoneNumber(val)
    if (isControlled) onChangeValue?.(val)
  }

  const inputBorder =
    theme === 'dark' ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(16,24,40,0.06)'

  const limitClassName = `chat-input-limit${showLimitError ? ' chat-input-limit--error' : ''}`

  return (
    <>
      <div
        className={`chat-input-container ${showLimitError ? 'chat-input-container--has-error' : 'chat-input-container--normal'}`}
      >
        <NativeVoiceListeningIndicator isListening={isRecording} isTranscribing={isTranscribing} />

        <div style={{position: 'relative', width: '100%'}}>
          <form onSubmit={handleSubmit} style={{width: '100%'}}>
            {isPhone ? (
              <div className='chat-input-controls'>
                <select
                  value={selectedDialCode}
                  onChange={(e) => setSelectedDialCode(e.target.value)}
                  className='form-select'
                  style={{
                    width: '110px',
                    borderRadius: '12px',
                    border: inputBorder,
                    backgroundColor: 'var(--bs-app-bg-color)',
                    color: 'var(--bs-app-text-color)',
                    height: '44px',
                  }}
                  disabled={disabled}
                >
                  {normalizedPhoneOptions.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
                <div style={{position: 'relative', flex: 1}}>
                  <input
                    ref={inputElementRef}
                    type='tel'
                    className='form-control'
                    placeholder={intl.formatMessage({
                      id: 'CHAT.INPUT.PHONE_PLACEHOLDER',
                    })}
                    value={phoneNumber}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    onFocus={() => {
                      try {
                        if (!disabled && onFocus) onFocus()
                      } catch (err) {
                        calculateErrorMessage(err)
                      }
                    }}
                    onClick={() => {
                      try {
                        if (!disabled && onFocus) onFocus()
                      } catch (err) {
                        calculateErrorMessage(err)
                      }
                    }}
                    style={{
                      backgroundColor: 'var(--bs-app-bg-color)',
                      color: 'var(--bs-app-text-color)',
                      paddingRight: '56px',
                      borderRadius: '12px',
                      border: inputBorder,
                      height: '44px',
                      boxShadow: 'none',
                    }}
                    disabled={disabled}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      right: '6px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                    }}
                  >
                    <button
                      type='submit'
                      onMouseEnter={() => setSendHover(true)}
                      onMouseLeave={() => setSendHover(false)}
                      style={{
                        width: 36,
                        height: 36,
                        padding: 0,
                        borderRadius: 18,
                        border: 'none',
                        background:
                          sendHover && theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'transparent',
                        color: sendHover ? '#007acc' : '#009ef7',
                        transform: sendHover ? 'translateY(-3px) scale(1.04)' : 'none',
                        transition:
                          'transform 140ms ease, color 140ms ease, box-shadow 140ms ease, background-color 140ms ease',
                        cursor: !phoneNumber.trim() || disabled ? 'not-allowed' : 'pointer',
                      }}
                      title={intl.formatMessage({id: 'CHAT.BUTTON.SEND'})}
                      disabled={!phoneNumber.trim() || disabled}
                    >
                      <Send size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className='chatbot-input-shell'>
                <div className='chatbot-input-field-wrap'>
                  <input
                    ref={inputElementRef}
                    type='text'
                    className={`chat-input-field${showAttachment ? '' : ' chat-input-field--no-attach'}`}
                    placeholder={intl.formatMessage({
                      id: 'CHAT.INPUT.PLACEHOLDER',
                    })}
                    style={
                      checkTranscript
                        ? {
                            boxShadow: `0 0 0 2px ${LOW_CONFIDENCE_BORDER}`,
                            borderColor: LOW_CONFIDENCE_BORDER,
                          }
                        : undefined
                    }
                    aria-describedby={checkTranscript ? 'chat-input-check-transcript' : undefined}
                    value={displayedValue}
                    onChange={(e) => handleChange(e.target.value)}
                    onFocus={() => {
                      try {
                        if (!disabled && onFocus) onFocus()
                      } catch (e) {
                        calculateErrorMessage(e)
                      }
                    }}
                    onClick={() => {
                      try {
                        if (!disabled && onFocus) onFocus()
                      } catch (e) {
                        calculateErrorMessage(e)
                      }
                    }}
                    disabled={disabled}
                  />
                  {showAttachment ? (
                    <button
                      type='button'
                      className='chatbot-input-attach'
                      tabIndex={-1}
                      aria-hidden='true'
                      disabled
                    >
                      <Paperclip size={18} />
                    </button>
                  ) : null}
                </div>

                {showMicButton && (
                  <VoiceMicButton
                    isListening={false}
                    disabled={disabled}
                    onClick={handleVoiceToggle}
                    hasError={dictationStatus === 'error'}
                    className={showSendButton ? 'voice-mic-button--secondary' : ''}
                  />
                )}

                {isRecording && (
                  <button
                    type='button'
                    onClick={() => void stopRecording()}
                    disabled={disabled}
                    className='voice-stop-button'
                    aria-label={intl.formatMessage({id: 'CHAT.VOICE.STOP'})}
                  >
                    <i className='fas fa-stop' style={{fontSize: 20}}></i>
                  </button>
                )}

                {showSendButton && (
                  <button
                    type='submit'
                    onMouseEnter={() => setSendHover(true)}
                    onMouseLeave={() => setSendHover(false)}
                    className={`chat-send-button ${
                      !displayedValue.trim() || disabled
                        ? 'chat-send-button--disabled'
                        : 'chat-send-button--enabled'
                    }`}
                    title={intl.formatMessage({id: 'CHAT.BUTTON.SEND'})}
                    disabled={!displayedValue.trim() || disabled}
                    aria-label={intl.formatMessage({id: 'CHAT.BUTTON.SEND'})}
                  >
                    <Send size={20} />
                  </button>
                )}
              </div>
            )}
          </form>
        </div>
      </div>
      <div className={limitClassName}>
        {showLimitError
          ? intl.formatMessage({id: 'CHAT.INPUT.MAX_LENGTH_ERROR'}, {max: maxLength})
          : intl.formatMessage(
              {id: 'CHAT.LIMIT.COUNTER'},
              {current: isPhone ? phoneNumber.length : displayedValue.length, max: maxLength}
            )}
      </div>
      {voiceErrorText ? (
        <div className='chat-input-voice-error' role='alert'>
          {voiceErrorText}
        </div>
      ) : null}
      {checkTranscript && !voiceErrorText ? (
        <div
          id='chat-input-check-transcript'
          className='chat-input-voice-error'
          role='status'
          style={{color: LOW_CONFIDENCE_BORDER}}
        >
          {intl.formatMessage({
            id: 'CHAT.VOICE.CHECK_TRANSCRIPT',
            defaultMessage: 'Please check the transcript before sending.',
          })}
        </div>
      ) : null}
    </>
  )
}

export default ChatInput
